<template>
  <div class="life-page" v-loading="loading">
    <section class="life-panel" v-if="order">
      <div class="life-panel__head">
        <h2 class="life-panel__title">订单详情 {{ order.orderId }}</h2>
        <div>
          <el-button @click="$router.back()">返回</el-button>
          <el-button v-if="canVerify && order.orderStatus === 'BOOKED'" type="success" @click="openVerify">核销</el-button>
          <el-button v-if="canCancel && (order.orderStatus === 'PENDING_PAYMENT' || order.orderStatus === 'BOOKED')" type="warning" @click="openCancel">取消/退款</el-button>
          <el-button v-if="canCancel && order.orderStatus === 'REFUND_FAILED'" type="danger" @click="handleRetry">重试退款</el-button>
        </div>
      </div>

      <el-descriptions title="商品与批次快照" :column="2" border>
        <el-descriptions-item label="商品">{{ order.productNameSnapshot }}</el-descriptions-item>
        <el-descriptions-item label="出发时间">{{ order.departureTimeSnapshot }}</el-descriptions-item>
        <el-descriptions-item label="单价">{{ fenToYuan(order.unitPriceFen) }}</el-descriptions-item>
        <el-descriptions-item label="人数">{{ order.travelerCount }}</el-descriptions-item>
        <el-descriptions-item label="应付总额">{{ fenToYuan(order.totalAmountFen) }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ order.remark || '-' }}</el-descriptions-item>
      </el-descriptions>

      <el-descriptions title="联系人" :column="2" border style="margin-top: 16px">
        <el-descriptions-item label="姓名">{{ order.contactName }}</el-descriptions-item>
        <el-descriptions-item label="手机号">
          {{ phoneText }}
          <el-button v-if="canReveal" type="text" @click="revealPhone">查看完整号码</el-button>
        </el-descriptions-item>
      </el-descriptions>

      <el-descriptions title="支付信息" :column="2" border style="margin-top: 16px">
        <el-descriptions-item label="支付状态">{{ order.payStatus }}</el-descriptions-item>
        <el-descriptions-item label="业务状态">{{ order.orderStatus }}</el-descriptions-item>
        <el-descriptions-item label="支付单号">{{ order.payOrderId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="渠道交易号">{{ order.transactionId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="支付时间">{{ order.paidAt || '-' }}</el-descriptions-item>
        <el-descriptions-item label="过期时间">{{ order.expireAt || '-' }}</el-descriptions-item>
      </el-descriptions>

      <el-descriptions title="核销信息" :column="2" border style="margin-top: 16px">
        <el-descriptions-item label="核销人">{{ order.verifiedBy || '-' }}</el-descriptions-item>
        <el-descriptions-item label="核销时间">{{ order.verifiedAt || '-' }}</el-descriptions-item>
      </el-descriptions>

      <el-descriptions v-if="order.refund" title="退款信息" :column="2" border style="margin-top: 16px">
        <el-descriptions-item label="退款单号">{{ order.refund.refundId }}</el-descriptions-item>
        <el-descriptions-item label="退款金额">{{ fenToYuan(order.refund.refundAmountFen) }}</el-descriptions-item>
        <el-descriptions-item label="退款状态">{{ order.refund.refundStatus }}</el-descriptions-item>
        <el-descriptions-item label="场景">{{ order.refund.refundScene }}</el-descriptions-item>
        <el-descriptions-item label="原因">{{ order.refund.reason || order.cancelReason || '-' }}</el-descriptions-item>
        <el-descriptions-item label="失败原因">{{ order.refund.failureReason || '-' }}</el-descriptions-item>
      </el-descriptions>

      <h3 style="margin-top: 20px">订单事件</h3>
      <el-timeline>
        <el-timeline-item
          v-for="item in (order.events || [])"
          :key="item.eventId"
          :timestamp="item.occurredAt || item.createTime"
        >
          {{ item.eventType }} / {{ item.source }}
          <span v-if="item.fromStatus">：{{ item.fromStatus }} → {{ item.toStatus }}</span>
        </el-timeline-item>
      </el-timeline>
    </section>

    <el-dialog title="核销订单" :visible.sync="verifyVisible" width="420px">
      <el-form label-width="90px">
        <el-form-item label="核销码">
          <el-input v-model="verifyCode" placeholder="扫码令牌或6位备用码" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="verifyVisible = false">取消</el-button>
        <el-button type="primary" :loading="acting" @click="submitVerify">确认核销</el-button>
      </span>
    </el-dialog>

    <el-dialog title="取消订单" :visible.sync="cancelVisible" width="480px">
      <el-form label-width="90px">
        <el-form-item label="取消原因">
          <el-input v-model="cancelReason" type="textarea" :rows="3" maxlength="300" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="cancelVisible = false">关闭</el-button>
        <el-button type="primary" :loading="acting" @click="submitCancel">确认</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getOrder, verifyOrder, cancelOrder, retryRefund, revealContactPhone } from '@/api/travel/travelApi'

export default {
  name: 'TravelOrderDetail',
  data() {
    return {
      loading: false,
      acting: false,
      order: null,
      phoneText: '',
      verifyVisible: false,
      cancelVisible: false,
      verifyCode: '',
      cancelReason: ''
    }
  },
  computed: {
    canVerify() {
      return typeof this.hasPrivilege === 'function' ? this.hasPrivilege('502026092200000202') : true
    },
    canCancel() {
      return typeof this.hasPrivilege === 'function' ? this.hasPrivilege('502026092200000203') : true
    },
    canReveal() {
      return typeof this.hasPrivilege === 'function' ? this.hasPrivilege('502026092200000204') : true
    }
  },
  created() {
    this.load()
  },
  methods: {
    fenToYuan(fen) {
      if (fen === null || fen === undefined || fen === '') return '-'
      return (Number(fen) / 100).toFixed(2)
    },
    async load() {
      const orderId = this.$route.query.orderId
      if (!orderId) return
      this.loading = true
      try {
        const res = await getOrder({ orderId })
        this.order = res.data
        this.phoneText = (this.order && this.order.contactPhoneMask) || '****'
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openVerify() {
      this.verifyCode = ''
      this.verifyVisible = true
    },
    openCancel() {
      this.cancelReason = ''
      this.cancelVisible = true
    },
    async submitVerify() {
      this.acting = true
      try {
        const payload = { orderId: this.order.orderId }
        if (this.verifyCode && this.verifyCode.length === 6) {
          payload.verifyCode = this.verifyCode
        } else {
          payload.token = this.verifyCode
        }
        await verifyOrder(payload)
        this.$message.success('核销成功')
        this.verifyVisible = false
        this.load()
      } catch (e) {
        this.$message.error(e.message || '核销失败')
      } finally {
        this.acting = false
      }
    },
    async submitCancel() {
      if (!this.cancelReason) {
        this.$message.warning('请填写取消原因')
        return
      }
      this.acting = true
      try {
        await cancelOrder({ orderId: this.order.orderId, reason: this.cancelReason })
        this.$message.success('已提交取消')
        this.cancelVisible = false
        this.load()
      } catch (e) {
        this.$message.error(e.message || '取消失败')
      } finally {
        this.acting = false
      }
    },
    async handleRetry() {
      this.acting = true
      try {
        await retryRefund({ orderId: this.order.orderId })
        this.$message.success('已发起退款重试')
        this.load()
      } catch (e) {
        this.$message.error(e.message || '重试失败')
      } finally {
        this.acting = false
      }
    },
    async revealPhone() {
      try {
        const res = await revealContactPhone({ orderId: this.order.orderId })
        this.phoneText = res.data.contactPhone
      } catch (e) {
        this.$message.error(e.message || '查看失败')
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import './life.scss';
</style>
